// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/fiscal/components/NotaCobrancaEnvioCard.tsx
//
//  "Cobrança e envio" da NF-e autorizada: o que a autorização disparou
//  (títulos a receber, boletos no banco, e-mail ao cliente com XML + DANFE +
//  boletos), com "Refazer" para o que falhou e "Enviar e-mail" (inclusive para
//  outro endereço). Notas sem boleto automático mostram só o envio manual.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Mail, MinusCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getApiErrorMessage } from '@/lib/apiClient';
import { notaFiscalService } from '@/features/fiscal/services/notaFiscalService';
import type { NfePosAutorizacao } from '@/features/fiscal/types';

const ROTULO_SITUACAO: Record<string, string> = {
  EM_ABERTO: 'Em aberto', LIQUIDADO: 'Pago', BAIXA_SOLICITADA: 'Cancelamento solicitado', BAIXADO: 'Cancelado',
};

function Etapa({ nome, situacao }: { nome: string; situacao: string }) {
  const [Icone, cor, texto] =
    situacao === 'OK'             ? [CheckCircle2, 'text-green-600', 'Feito'] :
    situacao === 'ERRO'           ? [AlertTriangle, 'text-destructive', 'Com erro'] :
    situacao === 'PENDENTE'       ? [Clock, 'text-amber-600', 'Pendente'] :
                                    [MinusCircle, 'text-muted-foreground', 'Não se aplica'];
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icone className={`h-4 w-4 ${cor}`} />
      <span className="font-medium">{nome}</span>
      <span className={`text-xs ${cor}`}>{texto}</span>
    </div>
  );
}

const fmtCur = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (s: string | null) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—');

export function NotaCobrancaEnvioCard({ notaId }: { notaId: number }) {
  const qc = useQueryClient();
  const chave = ['nota-pos-autorizacao', notaId];
  const { data, isLoading } = useQuery({
    queryKey: chave,
    queryFn: () => notaFiscalService.posAutorizacao(notaId),
    enabled: notaId > 0,
  });
  const [emailOutro, setEmailOutro] = useState('');
  const [ocupado, setOcupado] = useState<'refazer' | 'email' | null>(null);

  const aplicar = (r: NfePosAutorizacao) => qc.setQueryData(chave, r);

  const refazer = async () => {
    setOcupado('refazer');
    try {
      const r = await notaFiscalService.processarPosAutorizacao(notaId);
      aplicar(r);
      toast.success('Cobrança e envio processados.');
    } catch (e) {
      toast.error('Não foi possível processar.', { description: getApiErrorMessage(e) });
    } finally { setOcupado(null); }
  };

  const enviarEmail = async () => {
    setOcupado('email');
    try {
      const r = await notaFiscalService.enviarEmail(notaId, emailOutro.trim() || undefined);
      aplicar(r);
      if (r.email === 'OK') toast.success(`E-mail enviado para ${r.emailDestino}.`);
      else toast.error('E-mail não enviado.', { description: r.mensagem ?? undefined });
    } catch (e) {
      toast.error('Não foi possível enviar o e-mail.', { description: getApiErrorMessage(e) });
    } finally { setOcupado(null); }
  };

  const temPendencia = data && [data.titulos, data.boletos, data.email].some((s) => s === 'ERRO' || s === 'PENDENTE');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Mail className="h-4 w-4" /> Cobrança e envio
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</p>
        ) : data ? (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <Etapa nome="Títulos a receber" situacao={data.titulos} />
              <Etapa nome="Boletos" situacao={data.boletos} />
              <Etapa nome="E-mail" situacao={data.email} />
            </div>
            {data.dtEmailEnviado && (
              <p className="text-xs text-muted-foreground">
                Enviado para {data.emailDestino} em {new Date(data.dtEmailEnviado).toLocaleString('pt-BR')}.
              </p>
            )}
            {data.boletosDaNota.length > 0 && (
              <div className="rounded-md border divide-y">
                {data.boletosDaNota.map((b) => (
                  <div key={b.contaReceberId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                    <span className="w-16 text-muted-foreground">Parc. {b.parcela}</span>
                    <span className="w-28">{fmtData(b.dtVencimento)}</span>
                    <span className="w-28 font-medium">{fmtCur(b.valor)}</span>
                    {b.nossoNumero
                      ? <Badge variant="outline">Nosso nº {b.nossoNumero}</Badge>
                      : <Badge variant="secondary">sem boleto</Badge>}
                    {b.situacao && <span className="text-xs text-muted-foreground">{ROTULO_SITUACAO[b.situacao] ?? b.situacao}</span>}
                    {b.linhaDigitavel && <code className="text-xs break-all">{b.linhaDigitavel}</code>}
                  </div>
                ))}
              </div>
            )}
            {data.mensagem && (
              <pre className="text-xs whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-muted-foreground">{data.mensagem}</pre>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            A autorização não gerou cobrança automática (a forma de pagamento não está como "Gerar boleto automático").
            Você pode enviar a NF-e por e-mail ao cliente.
          </p>
        )}

        <div className="flex flex-wrap items-end gap-2">
          {data && temPendencia && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={refazer} disabled={!!ocupado}>
              <RefreshCw className={`h-3.5 w-3.5 ${ocupado === 'refazer' ? 'animate-spin' : ''}`} /> Refazer o que faltou
            </Button>
          )}
          <Input className="h-9 w-64" type="email" placeholder="Outro e-mail (opcional)"
            value={emailOutro} onChange={(e) => setEmailOutro(e.target.value)} />
          <Button size="sm" className="gap-1.5" onClick={enviarEmail} disabled={!!ocupado}>
            {ocupado === 'email' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
            {data?.dtEmailEnviado ? 'Reenviar e-mail' : 'Enviar e-mail'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
